import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ContactErrors, ContactField } from '@/lib/contact'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { ARC, IconTile, PixelIcon } from '@/components/generals/arcade'
import { CONTACT_LIMITS, CONTACT_TOPICS, validateContact } from '@/lib/contact'
import { CONTACT_PAGE } from '@/components/landing/contact/data'
import { RESUME } from '@/components/landing/cabinet/data'

type Status = 'idle' | 'sending' | 'sent'

type Values = Record<ContactField | 'website', string>

const EMPTY: Values = { name: '', email: '', topic: CONTACT_TOPICS[0], message: '', website: '' }
const ORDER: ContactField[] = ['name', 'email', 'topic', 'message']
const EMAIL = RESUME.contact.find((c) => c.label === "Email")
const INPUT = cn(
    'w-full rounded-none border-0 bg-(--cab-cream-hi) px-3.5 pt-[13px] pb-3 text-base leading-snug text-(--cab-plum) outline-none select-text',
    'shadow-[inset_0_0_0_3px_var(--cab-plum)] placeholder:text-(--cab-plum)/55',
    'focus:shadow-[inset_0_0_0_3px_var(--cab-teal),4px_4px_0_var(--cab-plum)]',
    'aria-invalid:shadow-[inset_0_0_0_3px_var(--cab-rust)] aria-invalid:focus:shadow-[inset_0_0_0_3px_var(--cab-rust),4px_4px_0_var(--cab-plum)]'
)
const FIELD = 'm-0 flex min-w-0 flex-col gap-2.5 border-0 p-0'
const LABEL = cn(ARC.label, 'p-0 text-(--cab-plum)')
const ERROR = 'm-0 flex items-center gap-2.5 text-[15px] leading-snug before:size-2.5 before:flex-none before:bg-(--cab-rust) before:shadow-[0_0_0_2px_var(--cab-plum)] before:content-[""]'
const FAILED = "Your message couldn't be sent just now. Please try again in a moment."

const readFields = (data: unknown): ContactErrors | null => {
    if (!data || typeof data !== 'object' || !('fields' in data)) return null
    const fields = (data as { fields: unknown }).fields
    return fields && typeof fields === 'object' ? (fields as ContactErrors) : null
}

const ContactForm: FC = (): ReactNode => {
    const formRef = useRef<HTMLFormElement>(null)
    const sentRef = useRef<HTMLDivElement>(null)
    const [values, setValues] = useState<Values>(EMPTY)
    const [errors, setErrors] = useState<ContactErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [failure, setFailure] = useState('')
    const [receipt, setReceipt] = useState('')
    const [typed, setTyped] = useState(0)

    const update = (field: keyof Values) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target
        setValues((v) => ({ ...v, [field]: value }))
        if (field !== 'website') setErrors((er) => (er[field] ? { ...er, [field]: undefined } : er))
    }

    const focusFirst = (found: ContactErrors) => {
        const first = ORDER.find((f) => found[f])
        if (first) formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()
    }

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (status === 'sending') return
        setFailure('')

        const input = {
            name: values.name.trim(),
            email: values.email.trim(),
            topic: values.topic,
            message: values.message.trim()
        }
        const found = validateContact(input)
        if (Object.keys(found).length) {
            setErrors(found)
            focusFirst(found)
            return
        }

        setStatus('sending')
        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...input, website: values.website })
            })
            const data: unknown = await response.json().catch(() => null)

            if (response.ok) {
                setReceipt(`Thanks, ${input.name}. Your message is in, and I'll reply to ${input.email} within two working days.`)
                setTyped(0)
                setValues(EMPTY)
                setErrors({})
                setStatus('sent')
                return
            }

            const fields = response.status === 400 ? readFields(data) : null
            if (fields) {
                setErrors(fields)
                focusFirst(fields)
            } else {
                setFailure(FAILED)
            }
            setStatus('idle')
        } catch {
            setFailure(FAILED)
            setStatus('idle')
        }
    }

    // the confirmation types itself out like dialog text
    useEffect(() => {
        if (status !== 'sent') return
        sentRef.current?.focus()
        const full = receipt.length
        const step = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? full : 2
        let n = 0
        const id = window.setInterval(() => {
            n = Math.min(n + step, full)
            setTyped(n)
            if (n >= full) window.clearInterval(id)
        }, 18)
        return () => window.clearInterval(id)
    }, [status, receipt])

    const writeAnother = () => {
        setStatus('idle')
        window.requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[name="name"]')?.focus())
    }

    if (status === 'sent') {
        return (
            <div ref={sentRef} className='flex min-h-[360px] flex-col items-start justify-center gap-5 outline-none' tabIndex={-1}>
                <IconTile name='check' tone='sage' />
                <p className={cn(ARC.outline, 'm-0 text-2xl leading-tight md:text-[32px]')}>Message sent</p>
                <p className={cn(ARC.text, 'min-h-[3.2em]')}>
                    <span aria-hidden>{receipt.slice(0, typed)}</span>
                    {typed < receipt.length && <span className='cab-cursor' aria-hidden />}
                    <span className='sr-only'>{receipt}</span>
                </p>
                <div className='flex flex-wrap items-center gap-x-7 gap-y-4 pt-1 pr-2 pb-2 pl-1'>
                    <button type='button' className={ARC.btn} onClick={writeAnother}>
                        Write another
                    </button>
                </div>
            </div>
        )
    }

    const describe = (field: ContactField): string | undefined => (errors[field] ? `contact-${field}-error` : undefined)

    return (
        <form ref={formRef} className='flex flex-col gap-6' onSubmit={onSubmit} noValidate>
            <div className='grid gap-6 sm:grid-cols-2'>
                <div className={FIELD}>
                    <label htmlFor='contact-name' className={LABEL}>
                        Your name
                    </label>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        className={INPUT}
                        value={values.name}
                        onChange={update('name')}
                        maxLength={CONTACT_LIMITS.name}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={describe('name')}
                    />
                    {errors.name && (
                        <p id='contact-name-error' className={ERROR}>
                            {errors.name}
                        </p>
                    )}
                </div>

                <div className={FIELD}>
                    <label htmlFor='contact-email' className={LABEL}>
                        Email
                    </label>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        placeholder="you@studio.com"
                        className={INPUT}
                        value={values.email}
                        onChange={update('email')}
                        maxLength={CONTACT_LIMITS.email}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={describe('email')}
                    />
                    {errors.email && (
                        <p id='contact-email-error' className={ERROR}>
                            {errors.email}
                        </p>
                    )}
                </div>
            </div>

            <fieldset className={FIELD} aria-describedby={describe('topic')}>
                <legend className={LABEL}>What's it about?</legend>
                <div className='mt-2.5 flex flex-wrap gap-2.5'>
                    {CONTACT_TOPICS.map((topic) => (
                        <label key={topic} className='group relative inline-flex'>
                            <input
                                className='peer absolute inset-0 m-0 size-full cursor-pointer opacity-0'
                                type='radio'
                                name='topic'
                                value={topic}
                                checked={values.topic === topic}
                                onChange={update('topic')}
                            />
                            <span
                                className={cn(
                                    ARC.pixel,
                                    'inline-flex min-h-[42px] items-center bg-(--cab-cream-hi) px-3 pt-2.5 pb-2 text-xs tracking-[0.05em] shadow-[inset_0_0_0_3px_var(--cab-plum)] group-hover:bg-(--cab-tan)',
                                    'peer-checked:bg-(--cab-teal) peer-checked:text-(--cab-cream-hi) peer-checked:shadow-[inset_0_0_0_3px_var(--cab-plum),3px_3px_0_var(--cab-plum)]',
                                    'peer-focus-visible:outline-3 peer-focus-visible:outline-offset-[3px] peer-focus-visible:outline-dashed peer-focus-visible:outline-(--cab-plum)'
                                )}
                            >
                                {topic}
                            </span>
                        </label>
                    ))}
                </div>
                {errors.topic && (
                    <p id='contact-topic-error' className={ERROR}>
                        {errors.topic}
                    </p>
                )}
            </fieldset>

            <div className={FIELD}>
                <div className='flex items-baseline justify-between gap-3'>
                    <label htmlFor='contact-message' className={LABEL}>
                        Message
                    </label>
                    <span className={cn(ARC.pixel, 'text-[11px] opacity-70')} aria-hidden>
                        {values.message.length}/{CONTACT_LIMITS.message}
                    </span>
                </div>
                <textarea
                    id='contact-message'
                    name='message'
                    rows={7}
                    placeholder="What are you making, and where could I help?"
                    className={cn(INPUT, 'min-h-[190px] resize-y')}
                    value={values.message}
                    onChange={update('message')}
                    maxLength={CONTACT_LIMITS.message}
                    aria-invalid={errors.message ? true : undefined}
                    aria-describedby={describe('message')}
                />
                {errors.message && (
                    <p id='contact-message-error' className={ERROR}>
                        {errors.message}
                    </p>
                )}
            </div>

            {/* left empty by people, filled in by bots */}
            <div className='absolute -left-[10000px] size-px overflow-hidden' aria-hidden>
                <label htmlFor='contact-website'>Website</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {failure && (
                <div className='flex items-start gap-3 bg-(--cab-rust)/12 px-4 py-3.5 text-[15px] leading-normal shadow-[inset_0_0_0_3px_var(--cab-rust)]' role='alert'>
                    <p className='m-0'>
                        {failure}
                        {EMAIL?.href && (
                            <>
                                {' '}You can also email me at{' '}
                                <a href={EMAIL.href} className='text-(--cab-teal) underline decoration-2 underline-offset-4'>
                                    {EMAIL.value}
                                </a>
                                .
                            </>
                        )}
                    </p>
                </div>
            )}

            <div className='flex flex-wrap items-center gap-x-7 gap-y-4 pt-1 pr-2 pb-2 pl-1'>
                <button type='submit' className={cn(ARC.btn, ARC.btnPrimary)} disabled={status === 'sending'}>
                    {status === 'sending' ? "Sending" : "Send message"}
                    {status === 'sending' ? (
                        <span className='cab-cursor' aria-hidden />
                    ) : (
                        <PixelIcon name='right' className={ARC.glyph} />
                    )}
                </button>
                <p className='m-0 max-w-[34ch] text-sm leading-normal opacity-85'>{CONTACT_PAGE.formNote}</p>
            </div>
        </form>
    )
}

export default ContactForm
